
import { useEffect, useState, useMemo } from 'react';
import { Page, Header, Content, ContentHeader, SupportButton, Table } from '@backstage/core-components';
import {
  EntityListProvider,
  useEntityList,
  EntityKindFilter,
  EntityRefLink,
} from '@backstage/plugin-catalog-react';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Box, Grid, InputAdornment, LinearProgress, MenuItem, TextField } from '@material-ui/core';
import { projectPageTranslationRef } from '../../translation/project-page/project-page';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { useApi, identityApiRef , configApiRef} from '@backstage/core-plugin-api';
import SearchIcon from '@material-ui/icons/Search';

const PrefilteredTable = ({ kind = 'Project' }) => {
  const { updateFilters, loading, entities } = useEntityList();
  const { t } = useTranslationRef(projectPageTranslationRef);

  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  
  const config = useApi(configApiRef);
  const backendUrl = config.getString('backend.baseUrl');

  const [projectCodes, setProjectCodes] = useState<string[]>([]);
  
  useEffect(() => {
    const fetchGroups = async () => {
      const identity = await identityApi.getBackstageIdentity();      
      const userEntityRef = identity.userEntityRef;      

      // Obtener email del usuario desde el catálogo
      let userEmail = userEntityRef.split('/')[1];
      try {
        const userEntity = await catalogApi.getEntityByRef(userEntityRef);
        const profile = userEntity?.spec?.profile as any;
        if (profile?.email) {
          userEmail = profile.email;
        }
      } catch (err) {
        console.warn('⚠️ Could not fetch user entity, using username instead:', err);
      }
      
      // Check for impersonated user (for development/testing)
      const impersonatedFromStorage = localStorage.getItem('backstage.impersonatedUser');
      const impersonatedFromConfig = config.getOptionalString('openfga.impersonatedUser');
      const impersonatedUser = impersonatedFromStorage || impersonatedFromConfig;
      
      let effectiveUser = impersonatedUser || userEmail;
      if (impersonatedUser && impersonatedUser !== userEmail) {
        try {
          const impersonatedUserRef = `user:default/${impersonatedUser}`;
          const impersonatedUserEntity = await catalogApi.getEntityByRef(impersonatedUserRef);
          const impersonatedProfile = impersonatedUserEntity?.spec?.profile as any;
          if (impersonatedProfile?.email) {
            effectiveUser = impersonatedProfile.email;
          }
        } catch (err) {
          console.warn('⚠️ Could not fetch impersonated user entity, using username instead:', err);
        }
        console.log('🎭 Impersonating user:', effectiveUser);
      }
      
      // Llama a OFGA para sacar los proyectos del usuario
      const token = (await identityApi.getCredentials()).token;
      const response = await fetch(`${backendUrl}/api/openfga-client/list-user-entities`, {
        method: 'POST',
        headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
            user: "user:" + effectiveUser,
            type: "project"
        }),
      });
      const data = await response.json();
      const projectCodesFromFGA = (data.objects ?? [])
        .map((p: string) => {
          const match = p.match(/^project:(prj\d{7})$/i);
          return match ? match[1].toLowerCase() : null;
        })
        .filter(Boolean) as string[];
      console.log('Project codes from OpenFGA:', projectCodesFromFGA);
      setProjectCodes(projectCodesFromFGA);
    };
    fetchGroups();
  }, [identityApi, catalogApi]);
  
  // Aplica los filtros una vez al montar
  useEffect(() => {
    updateFilters({
      kind: new EntityKindFilter(kind, kind),
    });
  }, [kind, updateFilters]);


  // Columnas personalizadas
  const customColumns = [
    //
    // --- Identificación del proyecto ---
    //
    {
      title: t('projectTableColumns.number'),
      highlight: true,
      render: (row: any) => row.entity.spec?.number ?? '—',
    },
    {
      title: t('projectTableColumns.name'),
      highlight: true,
      render: (row: any) => (
        <EntityRefLink
          entityRef={row.entity}
          defaultKind={row.entity.kind ?? 'Project'}
          defaultNamespace="default"
        />
      ),
    },

    //
    // --- Atributos funcionales ---
    //
    {
      title: t('projectTableColumns.type'),
      render: (row: any) => row.entity.spec?.type ?? '—',
    },
    {
      title: t('projectTableColumns.lifecycle'),
      render: (row: any) => row.entity.spec?.lifecycle ?? '—',
    },
    {
      title: t('projectTableColumns.projectManager'),
      render: (row: any) => (
        row.entity.spec?.projectManager ? <EntityRefLink
          entityRef={row.entity.spec?.projectManager}
          defaultKind={'User'}
          defaultNamespace="default"
        /> : '—'
      ),
    },
    {
      title: t('projectTableColumns.priority'),
      render: (row: any) => row.entity.spec?.priority ?? '—',
    },

    //
    // --- Fechas ---
    //
    {
      title: t('projectTableColumns.startDatePlanned'),
      render: (row: any) => row.entity.spec?.startDatePlanned ?? '—',
    },
    {
      title: t('projectTableColumns.endDatePlanned'),
      render: (row: any) => row.entity.spec?.endDatePlanned ?? '—',
    },
    {
      title: t('projectTableColumns.startDateReal'),
      render: (row: any) => row.entity.spec?.startDateReal ?? '—',
    },
    {
      title: t('projectTableColumns.endDateReal'),
      render: (row: any) => row.entity.spec?.endDateReal ?? '—',
    },
    {
      title: t('projectTableColumns.status'),
      render: (row: any) => {
        const status = (row.entity.spec?.status ?? '').toLowerCase();

        const color =
          status.includes('verde') ? '#4CAF50' :      // verde
            status.includes('amarillo') ? '#FFC107' :       // amarillo
              status.includes('rojo') ? '#F44336' :        // rojo
                '#BDBDBD';                                  // gris por defecto

        return (
          <Box display="flex" alignItems="center">
            <Box
              style={{
                width: 15,
                height: 15,
                borderRadius: '50%',
                backgroundColor: color,
                marginRight: 8,
              }}
            />
            {/* {row.entity.spec?.status ?? '—'} */}
          </Box>
        );
      },
    },
    // ...existing code...
    {
      title: t('projectTableColumns.percentCompleted'),
      render: (row: any) => {
        const numberValue = (row.entity.spec?.percentCompleted as string).replace(',', '.');
        const percent = Number(numberValue ?? 0);

        return (
          <Box display="flex" alignItems="center" width={180}>
            <Box width="100%">
              <LinearProgress
                variant="determinate"
                value={percent}
              />
            </Box>
            {/* Contenedor fijo para el número */}
            <Box width="100%" textAlign="center">
              <strong>{percent}%</strong>
            </Box>
          </Box>
        );
      },
    },
  ];

  // Memoize filtered base entities to avoid changing reference on every render
  const filteredBaseEntities = useMemo(
    () => entities.filter(e => !e.spec?.parent),
    [entities]
  );

  // Filter by projectCodes (case-insensitive)
  const filteredEntities = useMemo(
    () => filteredBaseEntities.filter(e =>
      projectCodes.map(code => code.toUpperCase()).includes(String(e.spec?.number ?? '').toUpperCase())
    ),
    [filteredBaseEntities, projectCodes]
  );

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [lifecycleFilter, setLifecycleFilter] = useState('');

  const availableTypes = useMemo(() => {
    const types = filteredEntities.map(e => e.spec?.type).filter(Boolean);
    return Array.from(new Set(types));
  }, [filteredEntities]);

  const availableLifecycles = useMemo(() => {
    const lifecycles = filteredEntities.map(e => e.spec?.lifecycle).filter(Boolean);
    return Array.from(new Set(lifecycles));
  }, [filteredEntities]);

  const normalize = (text: string) =>
    text
      .toLowerCase()
      .normalize('NFD')                // separa acentos
      .replace(/[\u0300-\u036f]/g, '') // elimina acentos
      .replace(/\s+/g, ' ')           // colapsa espacios
      .trim();

  const filteredProjects = useMemo(() => {
    const normalizedSearch = normalize(search);

    return filteredEntities.filter(app => {
      const name = normalize(app.metadata?.name ?? '');
      const title = normalize(app.metadata?.title ?? '');
      const number = normalize(app.spec?.number?.toString() ?? '');

      const matchesSearch = !normalizedSearch || [name, title, number].some(field =>
        normalizedSearch.split(' ').every(word => field.includes(word)),
      );

      const matchesType = !typeFilter || app.spec?.type === typeFilter;
      const matchesLifecycle = !lifecycleFilter || app.spec?.lifecycle === lifecycleFilter;

      return matchesSearch && matchesType && matchesLifecycle;
    });
  }, [filteredEntities, search, typeFilter, lifecycleFilter]);


  return (
    <div style={{ height: '100vh', width: '99%' }}>
      <Grid container direction="row" spacing={2} alignItems="center" justifyContent="flex-end" style={{ marginBottom: '16px' }}>
        <Grid item xs>
          <TextField
            select
            label={t('projectPage.typeFilterLabel')}
            variant="outlined"
            size="small"
            fullWidth
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            style={{ minWidth: 160 }}
          >
            <MenuItem value="">{t('projectPage.selectAll')}</MenuItem>
            {availableTypes.map((type: any) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs>
          <TextField
            select
            label={t('projectPage.lifecycleFilterLabel')}
            variant="outlined"
            size="small"
            fullWidth
            value={lifecycleFilter}
            onChange={e => setLifecycleFilter(e.target.value)}
            style={{ minWidth: 160 }}
          >
            <MenuItem value="">{t('projectPage.selectAll')}</MenuItem>
            {availableLifecycles.map((lifecycle: any) => (
              <MenuItem key={lifecycle} value={lifecycle}>
                {lifecycle}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item>
          <TextField
            label={t('projectPage.searchPlaceholder')}
            variant="outlined"
            size="small"
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>
      <Table
        title={t('projectPage.title')}
        isLoading={loading}
        data={filteredProjects.map(e => ({ entity: e }))}
        columns={customColumns}
        options={{
          search: false,
          paging: true,
        }}
      />
    </div>
  );
};

export const ProjectsPage = () => {
  const { t } = useTranslationRef(projectPageTranslationRef);
  return (
    <Page themeId="Proyecto">
      <Header title={t('projectPage.title')} subtitle={t('projectPage.subtitle')} />

      {/* Optional sub-header for breadcrumbs, actions, etc. */}
      <ContentHeader>
        <SupportButton>{t('projectPage.supportButtonTitle')}</SupportButton>
      </ContentHeader>
      {/* noPadding evita márgenes/huecos alrededor */}
      <Content noPadding>
        {/* Sin CatalogFilterLayout => no hay panel de filtros */}
        <EntityListProvider>
          <PrefilteredTable kind="Project" />
        </EntityListProvider>
      </Content>
    </Page>
  )
};